import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchCurrentUser, type CurrentUser } from '@wundo/client/auth.ts';
import { useAllOrganisations } from '@/lib/useAllOrganisations.ts';
import type { Organisation } from '@/types/organisations.ts';
import {
  readStoredOrganisationChoice,
  writeStoredOrganisationChoice,
} from './activeOrganisationStorage.ts';

/*
 * The organisation the console is looking at, chosen once in the header picker.
 * `organisation` is always concrete; "All organisations" is a flag beside it, so a
 * page that needs one organisation falls back to the last one that was chosen.
 */
export interface ActiveOrganisation {
  /** Null only while the signed-in user is still being fetched. */
  organisation: Organisation | null;
  /** "All organisations" is chosen. Only pages that filter optionally honour it. */
  allOrganisations: boolean;
  /** Null chooses "All organisations". */
  setOrganisation: (organisation: Organisation | null) => void;
}

export const ActiveOrganisationContext = createContext<ActiveOrganisation | null>(null);

/*
 * Outside the provider this answers with the user's own organisation and no way to
 * change it, so a page rendered alone in a test does not need the shell.
 */
export function useActiveOrganisation(): ActiveOrganisation {
  const provided = useContext(ActiveOrganisationContext);
  const alone = useOwnOrganisation();

  return provided ?? alone;
}

/** The organisation a filter page narrows to; null means every organisation. */
export function useOrganisationFilter(): Organisation | null {
  const { organisation, allOrganisations } = useActiveOrganisation();

  return allOrganisations ? null : organisation;
}

function useOwnOrganisation(): ActiveOrganisation {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const own = useSessionOrganisation(user?.organisation);

  return useMemo(
    () => ({ organisation: own, allOrganisations: false, setOrganisation: noSwitching }),
    [own],
  );
}

function noSwitching(): void {}

/* Held rather than rebuilt: a fresh object each render would be a changed dependency to every page. */
function useSessionOrganisation(
  organisation: CurrentUser['organisation'] | undefined,
): Organisation | null {
  return useMemo(
    () =>
      organisation
        ? { id: organisation.id, name: organisation.name ?? '', type: organisation.type }
        : null,
    [organisation],
  );
}

/** The state behind the provider. Only `ActiveOrganisationProvider` calls this. */
export function useActiveOrganisationState(): ActiveOrganisation {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const own = useSessionOrganisation(user?.organisation);
  const superadmin = user?.role === 'superadmin';

  const [stored, setStored] = useState(readStoredOrganisationChoice);
  const chosen = stored.organisation;

  /* The list holds every organisation, so a remembered one missing from it was deleted. */
  const { data: all } = useAllOrganisations(superadmin);
  const gone =
    chosen !== null && all !== undefined && !all.some((one) => one.id === chosen.id);

  const setOrganisation = useCallback((next: Organisation | null) => {
    setStored((previous) => {
      const choice = next
        ? { organisation: next, all: false }
        : { organisation: previous.organisation, all: true };
      writeStoredOrganisationChoice(choice);
      return choice;
    });
  }, []);

  /* Forgotten in storage only; in state the render below already falls back to the user's own. */
  useEffect(() => {
    if (gone) writeStoredOrganisationChoice({ organisation: null, all: stored.all });
  }, [gone, stored.all]);

  /* Anyone but a superadmin has one organisation, whatever an older session left in storage. */
  const organisation = superadmin && chosen && !gone ? chosen : own;
  const allOrganisations = superadmin && stored.all;

  return useMemo(
    () => ({ organisation, allOrganisations, setOrganisation }),
    [organisation, allOrganisations, setOrganisation],
  );
}
